import { SECTIONS, type SectionId } from '../lib/sections';
import { NavIcon, SECTION_ICON } from './NavIcons';

/**
 * The sections, down the left.
 *
 * Collapsed it is a column of icons; a deliberate hover or the menu button
 * opens it to the label and the line under it saying what the section is for.
 * A number on an item means that section is waiting on the owner.
 */

const SUBS: Readonly<Record<SectionId, string>> = {
  control: 'Talk to Brunel, watch the work',
  engine: 'Live systems and read-only history',
  settings: 'Profile, computers, rules and projects',
};

// A speech bubble with a question: the suite's one Support door.
const SUPPORT_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4.5 6.5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-6l-4 3v-3h-1a2 2 0 0 1-2-2z" />
    <path d="M12 13.2v.01M10.4 8.9a1.7 1.7 0 0 1 3.2.8c0 1.2-1.6 1.4-1.6 2.5" />
  </svg>
);

const HELP_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M9.8 9.2a2.4 2.4 0 0 1 4.6.9c0 1.8-2.4 2-2.4 3.8M12 17.2h.01" />
  </svg>
);

export function Rail({
  section, badges, onSection, onHelp, supportHref,
}: {
  section: SectionId;
  badges: Readonly<Record<SectionId, number>>;
  onSection: (id: SectionId) => void;
  onHelp: () => void;
  /** CommandOS Support for this build and surface (lib/support-link). */
  supportHref: string;
}) {
  return (
    <nav className="rail" id="rail" aria-label="Sections">
      <div className="rail-scroll">
        {SECTIONS.map((item) => (
          <div key={item.id} className={section === item.id ? 'rail-group active' : 'rail-group'} data-section={item.id}>
            <button type="button" title={item.label} aria-label={item.label} aria-current={section === item.id ? 'page' : undefined}
              className={section === item.id ? 'rail-item active' : 'rail-item'}
              onClick={() => onSection(item.id)}>
              <span className="ri-icon"><NavIcon name={SECTION_ICON[item.id]} strokeWidth={1.6} /></span>
              <span className="ri-text">
                <span className="ri-label">{item.label}</span>
                <span className="ri-sub">{SUBS[item.id]}</span>
              </span>
              {badges[item.id] > 0 && <span className="pill count">{badges[item.id]}</span>}
            </button>
          </div>
        ))}
      </div>
      <div className="rail-help">
        <button type="button" title="How Brunel Works" aria-label="Open How Brunel Works"
          className="rail-item" onClick={onHelp}>
          <span className="ri-icon">{HELP_ICON}</span>
          <span className="ri-text">
            <span className="ri-label">Help</span>
            <span className="ri-sub">How Brunel Works</span>
          </span>
        </button>
        {/* A plain link, so Support always goes somewhere visible: CommandOS's Ticket Center
            opens in a new tab and asks for its own sign-in when needed. */}
        <a href={supportHref} target="_blank" rel="noopener noreferrer" title="Support - CommandOS Ticket Center"
          aria-label="Open Support in CommandOS" className="rail-item" data-support="commandos">
          <span className="ri-icon">{SUPPORT_ICON}</span>
          <span className="ri-text">
            <span className="ri-label">Support</span>
            <span className="ri-sub">Report a problem or ask a question</span>
          </span>
        </a>
      </div>
    </nav>
  );
}
